import React, { Suspense, lazy, ReactNode } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { ToastProvider } from './contexts/ToastContext';
import { SubdomainGuard } from './components/SubdomainGuard';
import NotFound from "pages/NotFound";
import ProtectedRoute from 'components/ProtectedRoute';
import LoadingSkeleton from './components/ui/LoadingSkeleton';

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-900">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
      <p className="text-white/60 text-sm">Loading...</p>
    </div>
  </div>
);

/**
 * Route component wrapper that handles lazy-loaded components with Suspense and error boundaries
 * This ensures that errors in lazy-loaded components are caught properly
 */
interface LazyRouteProps {
  element: React.ReactElement;
}

const LazyRoute: React.FC<LazyRouteProps> = ({ element }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      {element}
    </Suspense>
  </ErrorBoundary>
);

// Critical pages - load immediately (authentication, landing)
import LandingPage from 'pages/landing-page';
import Authentication from 'pages/authentication';
import ForgotPassword from 'pages/authentication/ForgotPassword';
import OAuthCallback from 'pages/authentication/OAuthCallback';
import OtpLogin from 'pages/authentication/OtpLogin';
import VerifyOtp from 'pages/authentication/VerifyOtp';

// Lazy load all other pages for better performance
const InstagramCallback = lazy(() => import('pages/auth/instagram/InstagramCallback'));
const FacebookWebhookCallback = lazy(() => import('pages/auth/FacebookWebhookCallback'));
const PrivacyPolicy = lazy(() => import('pages/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('pages/legal/TermsOfService'));
const CookiePolicy = lazy(() => import('pages/legal/CookiePolicy'));
const GDPR = lazy(() => import('pages/legal/GDPR'));
const HelpCenter = lazy(() => import('pages/help/HelpCenter'));
const CreatorGuide = lazy(() => import('pages/guides/CreatorGuide'));
const BrandGuide = lazy(() => import('pages/guides/BrandGuide'));
const APIDocs = lazy(() => import('pages/guides/APIDocs'));
const About = lazy(() => import('pages/company/About'));
const Careers = lazy(() => import('pages/company/Careers'));
const Blog = lazy(() => import('pages/company/Blog'));
const Press = lazy(() => import('pages/company/Press'));
const Analytics = lazy(() => import('pages/marketing/Analytics'));
const Pricing = lazy(() => import('pages/marketing/Pricing'));
const PaymentPage = lazy(() => import('pages/PaymentPage'));

// Dashboard pages - lazy loaded
const BrandDashboard = lazy(() => import('pages/brand-dashboard'));
const ExploreCreators = lazy(() => import('pages/brand-dashboard/creators/ExploreCreators'));
const CreatorProfile = lazy(() => import('pages/brand-dashboard/creators/CreatorProfile'));
const SavedCreators = lazy(() => import('pages/brand-dashboard/creators/SavedCreators'));
const CreatorRequests = lazy(() => import('pages/brand-dashboard/creators/CreatorRequests'));
const VerifiedCreators = lazy(() => import('pages/brand-dashboard/creators/VerifiedCreators'));
const AnalyticsDashboard = lazy(() => import('pages/brand-dashboard/analytics/AnalyticsDashboard'));
const BudgetDashboard = lazy(() => import('pages/brand-dashboard/budget/BudgetDashboard'));
const MessagesPage = lazy(() => import('pages/brand-dashboard/messages'));
const BrandSettings = lazy(() => import('pages/brand-dashboard/settings'));
const BrandInvitations = lazy(() => import('pages/brand-dashboard/invitations'));

// Creator Dashboard pages - lazy loaded
const CreatorDashboard = lazy(() => import('pages/creator-dashboard'));
const CreatorInvitations = lazy(() => import('pages/creator-dashboard/invitations/CreatorInvitations'));
const MyApplications = lazy(() => import('pages/creator-dashboard/applications/MyApplications'));
const SubmitWork = lazy(() => import('pages/creator-dashboard/applications/SubmitWork'));
const CreatorPortfolioPage = lazy(() => import('pages/creator-dashboard/portfolio/CreatorPortfolioPage'));
const EarningsDashboard = lazy(() => import('pages/creator-dashboard/earnings'));
const CreatorMessagesPage = lazy(() => import('pages/creator-dashboard/messages'));
const CreatorSettingsPage = lazy(() => import('pages/creator-dashboard/settings'));
const CreatorAnalyticsDashboard = lazy(() => import('pages/creator-dashboard/analytics'));
const CreatorSubscriptionPage = lazy(() => import('pages/creator-dashboard/subscription/CreatorSubscriptionPage'));
const AccountSafetyPage = lazy(() => import('pages/creator-dashboard/account-safety/AccountSafetyPage'));
const KYCVerificationPage = lazy(() => import('pages/creator-dashboard/kyc/KYCVerificationPage'));
const InMartPage = lazy(() => import('pages/creator-dashboard/inmart'));
const CreatorLeaderboardPage = lazy(() => import('pages/creator-dashboard/leaderboard'));

// Campaign pages
const CampaignApplications = lazy(() => import('pages/campaign-applications'));
const CampaignDetail = lazy(() => import('pages/campaign-detail'));
const CampaignsPage = lazy(() => import('pages/campaigns'));

// Community pages
const Communities = lazy(() => import('pages/community/Communities'));

// Admin & Agency
const AdminDashboard = lazy(() => import('pages/admin-dashboard'));
const AgencyDashboard = lazy(() => import('pages/agency-dashboard'));

// AI Chat Page
const AIChat = lazy(() => import('pages/ai-chat'));

// Testing & Other
const TestBackend = lazy(() => import('pages/test-backend'));
const TestOtp = lazy(() => import('pages/test-otp'));
const NotificationsPage = lazy(() => import('pages/notifications/NotificationsPage'));
const PublicCampaignPage = lazy(() => import('pages/public-campaign'));
const AllCampaignsPage = lazy(() => import('pages/all-campaigns'));
const CommunityFeed = lazy(() => import('pages/community'));
const UserProfile = lazy(() => import('pages/community/UserProfile'));
const CommunityNotifications = lazy(() => import('pages/community/NotificationsPage'));
const PostDetail = lazy(() => import('pages/community/PostDetail'));
const ExplorePage = lazy(() => import('pages/community/Explore'));
const CommunityMessagesPage = lazy(() => import('pages/community/MessagesPage'));
const JoinGroup = lazy(() => import('pages/community/JoinGroup'));

// SEO Landing Pages - for keyword targeting and AI discoverability
const InfluencerMarketingIndia = lazy(() => import('pages/seo/InfluencerMarketingIndia'));
const FindInfluencersIndia = lazy(() => import('pages/seo/FindInfluencersIndia'));
const MicroInfluencerMarketing = lazy(() => import('pages/seo/MicroInfluencerMarketing'));
const InstagramInfluencerPlatform = lazy(() => import('pages/seo/InstagramInfluencerPlatform'));
const YouTubeCreatorMarketplace = lazy(() => import('pages/seo/YouTubeCreatorMarketplace'));
const NoonxVsUpfluence = lazy(() => import('pages/seo/NoonxVsUpfluence'));
const UGCCreatorPlatform = lazy(() => import('pages/seo/UGCCreatorPlatform'));
const BrandAmbassadorPlatform = lazy(() => import('pages/seo/BrandAmbassadorPlatform'));

// Blog Pages - for content marketing and SEO
const BlogIndex = lazy(() => import('pages/blog/index'));
const InfluencerMarketingGuide = lazy(() => import('pages/blog/InfluencerMarketingGuide'));

// Tracking Pages - for NX Ads click tracking
const TrackingRedirect = lazy(() => import('pages/track'));

// Public Creator Portfolio - for public-facing creator profiles
const PublicCreatorPortfolio = lazy(() => import('pages/public-creator-portfolio/PublicCreatorPortfolio'));


const Routes: React.FC = () => {
  return (
    <BrowserRouter>
      <ToastProvider>
        <ErrorBoundary>
          <SubdomainGuard>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <RouterRoutes>
              {/* Define your routes here */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/authentication" element={<Authentication />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/otp-login" element={<OtpLogin />} />
            <Route path="/verify" element={<VerifyOtp />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route path="/auth/instagram/callback" element={<InstagramCallback />} />
            <Route path="/auth/v1/callback" element={<FacebookWebhookCallback />} />
            
            <Route path="/creators/:username" element={<PublicCreatorPortfolio />} />
            {/* Public Campaign Page - For sharing with creators */}
            <Route path="/c/:campaignId" element={<PublicCampaignPage />} />
            <Route path="/campaigns" element={<AllCampaignsPage />} />
            
            {/* NX Ads Tracking Links - For click tracking */}
            <Route path="/t/:code" element={<TrackingRedirect />} />
            <Route path="/track/:code" element={<TrackingRedirect />} />
            
            {/* Creator Community - Twitter-like feed */}
            <Route path="/community/notifications" element={<CommunityNotifications />} />
            <Route path="/community/communities" element={<Communities />} />
            <Route path="/community/messages" element={<CommunityMessagesPage />} />
            <Route path="/community/join/:inviteCode" element={<JoinGroup />} />
            <Route path="/community/post/:postId" element={<PostDetail />} />
            <Route path="/community/profile/:userId" element={<UserProfile />} />
            <Route path="/community/explore" element={<ExplorePage />} />
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/community" element={<CommunityFeed />} />
          
          {/* Testing Routes */}
          <Route path="/test-backend" element={<TestBackend />} />
          <Route path="/test-otp" element={<TestOtp />} />
          
          {/* Legal Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/gdpr" element={<GDPR />} />
          
          {/* Help Center */}
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help-center" element={<HelpCenter />} />
          <Route path="/help-center" element={<HelpCenter />} />
          
          {/* Guides */}
          <Route path="/creator-guide" element={<CreatorGuide />} />
          <Route path="/brand-guide" element={<BrandGuide />} />
          <Route path="/api" element={<APIDocs />} />
          
          {/* Company Pages */}
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/influencer-marketing-guide-india-2025" element={<InfluencerMarketingGuide />} />
          <Route path="/press" element={<Press />} />
          
          {/* Marketing Pages */}
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/pricing" element={<Pricing />} />
          
          {/* SEO Landing Pages - for organic search and AI discoverability */}
          <Route path="/influencer-marketing-india" element={<InfluencerMarketingIndia />} />
          <Route path="/find-influencers-india" element={<FindInfluencersIndia />} />
          <Route path="/micro-influencer-marketing" element={<MicroInfluencerMarketing />} />
          <Route path="/instagram-influencer-platform" element={<InstagramInfluencerPlatform />} />
          <Route path="/youtube-creator-marketplace" element={<YouTubeCreatorMarketplace />} />
          <Route path="/noonx-vs-upfluence" element={<NoonxVsUpfluence />} />
          <Route path="/ugc-creator-platform" element={<UGCCreatorPlatform />} />
          <Route path="/brand-ambassador-platform" element={<BrandAmbassadorPlatform />} />
          
          {/* Brand Dashboard - Protected for brand and admin users only */}
          <Route path="/brand-dashboard" element={
            <LazyRoute element={
              <ProtectedRoute allowedRoles={['brand', 'admin']}>
                <BrandDashboard />
              </ProtectedRoute>
            } />
          } />
        
        {/* Creators Section - Brand only */}
        <Route path="/brand-dashboard/creators" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <ExploreCreators />
            </ProtectedRoute>
          } />
        } />
        <Route path="/brand-dashboard/creators/saved" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <SavedCreators />
            </ProtectedRoute>
          } />
        } />
        <Route path="/brand-dashboard/creators/requests" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <CreatorRequests />
            </ProtectedRoute>
          } />
        } />
        <Route path="/brand-dashboard/creators/verified" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <VerifiedCreators />
            </ProtectedRoute>
          } />
        } />
        <Route path="/brand-dashboard/creators/:creatorId" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <CreatorProfile />
            </ProtectedRoute>
          } />
        } />
        
        {/* Analytics Dashboard - Brand only */}
        <Route path="/brand-dashboard/analytics" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <AnalyticsDashboard />
            </ProtectedRoute>
          } />
        } />
        
        {/* Budget Dashboard - Brand only */}
        <Route path="/brand-dashboard/budget" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <BudgetDashboard />
            </ProtectedRoute>
          } />
        } />
        
        {/* Messages - Brand only */}
        <Route path="/brand-dashboard/messages" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <MessagesPage />
            </ProtectedRoute>
          } />
        } />
        
        {/* Settings - Brand only */}
        <Route path="/brand-dashboard/settings" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <BrandSettings />
            </ProtectedRoute>
          } />
        } />
        <Route path="/brand-dashboard/invitations" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin']}>
              <BrandInvitations />
            </ProtectedRoute>
          } />
        } />
        
        {/* Payment Page - All authenticated users */}
        <Route path="/payment" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'admin', 'creator', 'agency']}>
              <PaymentPage />
            </ProtectedRoute>
          } />
        } />
        
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaign/:campaignId" element={<CampaignDetail />} />
        <Route path="/campaign-detail/:campaignId" element={<CampaignDetail />} />
        <Route path="/landing-page" element={<LandingPage />} />
        
        {/* Creator Dashboard - Protected for creator users only */}
        <Route path="/creator-dashboard" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <CreatorDashboard />
            </ProtectedRoute>
          } />
        } />
        <Route path="/creator-dashboard/invitations" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <CreatorInvitations />
            </ProtectedRoute>
          } />
        } />
        <Route path="/creator-dashboard/applications" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <MyApplications />
            </ProtectedRoute>
          } />
        } />
        <Route path="/creator-dashboard/applications/:applicationId/submit" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <SubmitWork />
            </ProtectedRoute>
          } />
        } />
        <Route path="/creator-dashboard/portfolio" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <CreatorPortfolioPage />
            </ProtectedRoute>
          } />
        } />

        {/* Earnings Dashboard - Creator only */}
        <Route path="/creator-dashboard/earnings" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <EarningsDashboard />
            </ProtectedRoute>
          } />
        } />

        {/* Analytics Dashboard - Creator only */}
        <Route path="/creator-dashboard/analytics" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <CreatorAnalyticsDashboard />
            </ProtectedRoute>
          } />
        } />

        {/* Subscription - Creator only */}
        <Route path="/creator-dashboard/subscription" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <CreatorSubscriptionPage />
            </ProtectedRoute>
          } />
        } />

        {/* InMart - Creator Marketplace (Coming Soon) */}
        <Route path="/creator-dashboard/inmart" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <InMartPage />
            </ProtectedRoute>
          } />
        } />

        {/* Leaderboard - Creator only */}
        <Route path="/creator-dashboard/leaderboard" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <CreatorLeaderboardPage />
            </ProtectedRoute>
          } />
        } />

        {/* Account Safety - Creator only */}
        <Route path="/creator-dashboard/account-safety" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <AccountSafetyPage />
            </ProtectedRoute>
          } />
        } />

        {/* KYC Verification - Creator only */}
        <Route path="/creator-dashboard/kyc" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <KYCVerificationPage />
            </ProtectedRoute>
          } />
        } />

        {/* Messages - Creator only */}
        <Route path="/creator-dashboard/messages" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <CreatorMessagesPage />
            </ProtectedRoute>
          } />
        } />

        {/* Settings - Creator only */}
        <Route path="/creator-dashboard/settings" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['creator']}>
              <CreatorSettingsPage />
            </ProtectedRoute>
          } />
        } />
        
        {/* Admin Dashboard - Protected for admin users only */}
        <Route path="/admin-dashboard" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
        } />
        
        {/* Agency Dashboard - Protected for agency users only */}
        <Route path="/agency-dashboard" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['agency', 'admin']}>
              <AgencyDashboard />
            </ProtectedRoute>
          } />
        } />
        
        {/* AI Chat - ChatGPT-style full page AI */}
        <Route path="/ai-chat" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'creator', 'agency', 'admin']}>
              <AIChat />
            </ProtectedRoute>
          } />
        } />
        
        <Route path="/campaign-applications" element={<CampaignApplications />} />
        <Route path="/test-backend" element={<TestBackend />} />
        
        {/* Notifications - All authenticated users */}
        <Route path="/notifications" element={
          <LazyRoute element={
            <ProtectedRoute allowedRoles={['brand', 'creator', 'admin']}>
              <NotificationsPage />
            </ProtectedRoute>
          } />
        } />
        
        {/* User Profile - Must be before 404 but after all other routes */}
        <Route path="/:username" element={<UserProfile />} />
        
        <Route path="*" element={<NotFound />} />
          </RouterRoutes>
            </Suspense>
          </SubdomainGuard>
        </ErrorBoundary>
      </ToastProvider>
    </BrowserRouter>
  );
};

export default Routes;